import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ArrowRight, CheckCircle, Lightning, MagnifyingGlass, Target, TrendUp, Warning } from "@phosphor-icons/react";

const SUGGESTED = ["freedomcare.com", "brevy.com/caregiver/pennsylvania/how-to-get-paid-family-caregiver", "paid.care/family-caregiver-pay-rate-in-pa", "givers.com/how-to-get-paid-caregiver/pennsylvania", "chosenfamilyhomecare.com", "ameribesthomecare.com"];

function AnalysisCard({ a, testid }) {
  const x = a.analysis || {};
  const List = ({ items, icon: Icon, cls }) => (
    <ul className="mt-2 space-y-1.5">
      {(items || []).map((t, i) => (
        <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-stone-700">
          <Icon size={15} weight="fill" className={`mt-0.5 shrink-0 ${cls}`} /> {t}
        </li>
      ))}
    </ul>
  );
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8" data-testid={testid}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="font-heading text-2xl font-medium tracking-tight text-[#23282f]" data-testid={`${testid}-company`}>{x.company || a.url}</p>
          <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-primary underline decoration-primary/30 underline-offset-2">{a.url}</a>
        </div>
        <p className="text-xs text-stone-400">{new Date(a.analyzed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
      </div>
      {x.headline && (
        <div className="mt-4 rounded-xl bg-stone-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-400">Their headline</p>
          <p className="mt-1 font-heading text-lg font-medium text-stone-900">"{x.headline}"</p>
          {x.offer && <p className="mt-1 text-sm text-stone-600">{x.offer}</p>}
        </div>
      )}
      {(x.ctas || []).length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-400">Their calls to action</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {x.ctas.map((c, i) => <span key={i} className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-semibold text-primary">{c}</span>)}
          </div>
        </div>
      )}
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#1e8449]"><CheckCircle size={14} weight="fill" /> What to borrow</p>
          <List items={x.what_to_borrow} icon={CheckCircle} cls="text-[#1e8449]" />
        </div>
        <div>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#8a5d14]"><Warning size={14} weight="fill" /> Gaps to exploit</p>
          <List items={x.gaps_to_exploit} icon={Warning} cls="text-[#8a5d14]" />
        </div>
      </div>
      {(x.funnel_structure || []).length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wide text-stone-500">Their page structure</p>
          <ol className="mt-2 space-y-1">
            {x.funnel_structure.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-stone-100 text-[10px] font-bold text-stone-500">{i + 1}</span>{s}
              </li>
            ))}
          </ol>
        </div>
      )}
      {x.ad_angle && (
        <div className="mt-5 rounded-xl border border-primary/25 bg-primary/[0.04] p-4" data-testid={`${testid}-ad-angle`}>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary"><Lightning size={14} weight="fill" /> Ad angle to try</p>
          <p className="mt-1.5 text-sm leading-relaxed text-stone-800">{x.ad_angle}</p>
        </div>
      )}
    </div>
  );
}

export default function CompetitionPage() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [fresh, setFresh] = useState(null);

  const load = () => api.get("/admin/competition/analyses").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const analyze = async (target) => {
    const u = (target || url).trim();
    if (!u) return;
    setBusy(true);
    setFresh(null);
    try {
      const { data } = await api.post("/admin/competition/analyze", { url: u }, { timeout: 120000 });
      setFresh(data);
      setUrl("");
      load();
      toast.success("Analysis ready");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6" data-testid="competition-page">
      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-400">Growth & marketing</p>
      <h1 className="mt-1 font-heading text-3xl font-medium tracking-tight text-[#23282f] sm:text-4xl">Look at your competition</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-stone-500">
        Drop in any competitor's page — their funnel, their CTAs, their tactics — and get back what to borrow,
        what gaps to exploit, and an ad angle to try. Built for your Facebook ad campaigns.
      </p>

      <div className="mt-6 flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="competitor.com or a full page URL"
          className="h-12 border-stone-300 bg-white text-base" data-testid="competitor-url"
          onKeyDown={(e) => e.key === "Enter" && analyze()} />
        <Button onClick={() => analyze()} disabled={busy || !url.trim()}
          className="h-12 shrink-0 rounded-full bg-primary px-6 text-primary-foreground hover:bg-stone-900" data-testid="analyze-button">
          {busy ? "Reading their page…" : <><MagnifyingGlass size={16} className="mr-1.5" /> Analyze</>}
        </Button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-stone-400">Try:</span>
        {SUGGESTED.map((s) => (
          <button key={s} onClick={() => analyze(s)} disabled={busy}
            className="rounded-full border border-stone-200 px-3 py-1 text-xs text-stone-500 transition-colors hover:border-primary/40 hover:text-primary"
            data-testid={`suggested-${s.split("/")[0].replace(/\W+/g, "-")}`}>
            {s.split("/")[0]}
          </button>
        ))}
      </div>

      {busy && (
        <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-10 text-center text-sm text-stone-400" data-testid="analyzing-state">
          Reading their page and breaking down the funnel…
        </div>
      )}

      {fresh && <div className="mt-8"><AnalysisCard a={fresh} testid="analysis-fresh" /></div>}

      {rows.length > 0 && (
        <div className="mt-10">
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-stone-500">
            <TrendUp size={14} /> Past analyses
          </p>
          <div className="mt-4 space-y-6">
            {rows.filter((r) => r.id !== fresh?.id).map((r) => <AnalysisCard key={r.id} a={r} testid={`analysis-${r.id}`} />)}
          </div>
        </div>
      )}
    </div>
  );
}
